import { ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RecipeBreadcrumbService } from '@woolworthsnz/recipe';
import { InfiniteScrollingService, SearchMediatorService } from '@woolworthsnz/search';
import {
	DatalayerService,
	MetaOptions,
	ModalEvent,
	ModalOverlayService,
	NotificationType,
	PageMeta,
} from '@woolworthsnz/styleguide';
import { RecipeListRequest } from '@woolworthsnz/trader-api';
import { mapToStamps } from 'libs/recipe/src/lib/helpers/stamp.helper';
import { MonoTypeOperatorFunction, Observable, Subject, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, skip, take, takeUntil } from 'rxjs/operators';
import { RecipePageType, RecipesState, RecipesStoreService } from '../../services/recipes-store.service';

export class RecipesResultsBasePage implements PageMeta {
	state$: Observable<RecipesState>;
	asyncMeta$ = new Subject<MetaOptions>();

	protected sortOption: RecipeListRequest.SortEnum;

	private intersectionObserver: IntersectionObserver | undefined;
	private disconnected = new Subject();

	constructor(
		protected recipesStore: RecipesStoreService,
		protected route: ActivatedRoute,
		protected router: Router,
		protected modalOverlayService: ModalOverlayService,
		protected searchMediatorService: SearchMediatorService,
		protected infiniteScrollingService: InfiniteScrollingService,
		protected datalayerService: DatalayerService,
		protected pageType: RecipePageType,
		protected recipeBreadcrumbService: RecipeBreadcrumbService
	) {
		this.state$ = recipesStore.state$;
		this.searchMediatorService.loadMore$.pipe(takeUntil(this.disconnected)).subscribe(() => this.loadMore());
	}

	mapToStamps = mapToStamps;

	initialize(): void {
		const queryParams = this.route.snapshot.queryParamMap;
		this.sortOption =
			(queryParams.get('sort') as RecipeListRequest.SortEnum) ||
			this.recipesStore.state.currentSortOption ||
			RecipeListRequest.SortEnum.Latest;
		this.recipesStore.loadSavedList().subscribe();

		this.recipesStore.state$
			.pipe(
				takeUntil(this.disconnected),
				filter((state) => !!state.category || !!state.searchQuery),
				distinctUntilChanged(
					(a, b) => a?.category?.description === b?.category?.description && a?.searchQuery === b?.searchQuery
				)
			)
			.subscribe((res) => {
				if (res.searchQuery) {
					this.asyncMeta$.next({
						pageTitle: 'recipeSearch',
						pageTitleName: res.searchQuery,
						metaTitle: 'recipeSearch',
						metaTitleName: res.searchQuery,
						metaDescription: 'recipeSearch',
						metaDescriptionName: res.searchQuery,
					});
				} else {
					this.asyncMeta$.next({
						pageTitle: 'recipeCategory',
						pageTitleName: res.category?.description,
						metaTitle: 'recipeCategory',
						metaTitleName: res.category?.description,
						metaDescription: 'recipeCategory',
						metaDescriptionName: res.category?.description,
					});
				}
				this.recipeBreadcrumbService.setBreadcrumbs(undefined, res.category);
			});
	}

	disconnect(): void {
		this.disconnected.next(undefined);
		this.disconnected.complete();
		this.intersectionObserver?.disconnect();
	}

	loadMore(): void {
		this.datalayerService.trackRecipeEvent(NotificationType.Button, 'R_load_more');
		this.recipesStore.loadMore(this.pageType).pipe(this.takeUntilRouteChange()).subscribe();
	}

	sortChange(sort: string): void {
		this.recipesStore
			.sortChange(sort as RecipeListRequest.SortEnum, this.pageType)
			.pipe(this.takeUntilRouteChange())
			.subscribe();

		this.router.navigate([], {
			queryParams: { sort },
			queryParamsHandling: 'merge',
			replaceUrl: false,
		});
	}

	handleColumnCountChange(event: number): void {
		this.recipesStore.setRecipeColumns(event);
	}

	handleSave({ recipeId }: { recipeId: number }): void {
		this.recipesStore
			.saveRecipe(recipeId)
			.pipe(
				take(1),
				catchError((err) => {
					if (err.status && err.status === 401) {
						this.modalOverlayService.open({
							eventType: ModalEvent.saveRecipe,
						});
					}
					return throwError(err);
				})
			)
			.subscribe();
	}

	setupScroller(scrollAnchor: ElementRef<HTMLElement>): void {
		// disconnect any existing ones
		this.intersectionObserver?.disconnect();

		if (!scrollAnchor) {
			return;
		}

		this.intersectionObserver = this.infiniteScrollingService.setupInfiniteScroller(
			scrollAnchor.nativeElement,
			() => {
				const state = this.recipesStore.state as RecipesState;
				return state.recipes.length < state.totalRecipes;
			}
		);
	}

	/**
	 * Cancels observable when route changes
	 */
	takeUntilRouteChange = (): MonoTypeOperatorFunction<unknown> => takeUntil(this.route.paramMap.pipe(skip(1)));
}
